import CamerasYamlEditor from "./scenes/config/CamerasYamlEditor";
import DetectionsYamlEditor from "./scenes/config/DetectionsYamlEditor";
import NotificationContacts from "./scenes/config/NotificationContacts";
import VisualAIConfig from "./scenes/config/VisualAIConfig";
import "bootstrap/dist/css/bootstrap.min.css";
import Billing from "./scenes/billing/billing";
import { ColorModeContext, useMode } from "./theme";
import { Alert, Box, Button, CircularProgress, CssBaseline, Stack, ThemeProvider } from "@mui/material";
import "./App.css";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Calendar from "./scenes/calendar/calendar";
import FAQ from "./scenes/faq";
import Settings from "./scenes/dashboard/settings";
import ManageFiles from "./scenes/managefiles";
import AuthWrapper from "./components/AuthWrapper";
import Users from "./scenes/users";
import VigilanteDashboard from "./scenes/vigilante/VigilanteDashboard";
import AuditLog from "./scenes/saas/AuditLog";
import CameraAIConfig from "./scenes/saas/CameraAIConfig";
import Onboarding from "./scenes/saas/Onboarding";
import ProfessionalAlerts from "./scenes/saas/ProfessionalAlerts";
import SuperadminConsole from "./scenes/saas/SuperadminConsole";
import SupportCenter from "./scenes/saas/SupportCenter";
import SystemHealth from "./scenes/saas/SystemHealth";
import UnifiedCameras from "./scenes/saas/UnifiedCameras";
import { PlatformProvider } from "./context/PlatformContext";
import AnalyticsModulePage from "./modules/analytics/AnalyticsModulePage";
import AiPerformancePage from "./modules/analytics/AiPerformancePage";
import LossPreventionPage from "./modules/analytics/LossPreventionPage";
import DetectionReportsPage from "./modules/analytics/DetectionReportsPage";
import StoreComparisonPage from "./modules/analytics/StoreComparisonPage";
import CasesPage from "./modules/incidents/CasesPage";
import EvidencePage from "./modules/incidents/EvidencePage";
import IncidentsModulePage from "./modules/incidents/IncidentsModulePage";
import TimelinePage from "./modules/incidents/TimelinePage";
import ForensicSearchPage from "./modules/intelligence/ForensicSearchPage";
import HeatmapsPage from "./modules/intelligence/HeatmapsPage";
import IntelligenceMapPage from "./modules/intelligence/IntelligenceMapPage";
import PersonJourneyPage from "./modules/intelligence/PersonJourneyPage";
import CameraHealthPage from "./modules/monitoring/CameraHealthPage";
import MonitoringModulePage from "./modules/monitoring/MonitoringModulePage";
import RecordingsPage from "./modules/monitoring/RecordingsPage";
import StoreMapPage from "./modules/monitoring/StoreMapPage";
import ExecutiveOverview from "./modules/overview/ExecutiveOverview";
import OperationsCenter from "./modules/monitoring/OperationsCenter";
import SystemAdvancedPage from "./modules/system/SystemAdvancedPage";
import SystemModulePage from "./modules/system/SystemModulePage";
import EdgeDevicesPage from "./modules/system/EdgeDevicesPage";
import AppShell from "./shell/AppShell";
import SelfServicePortal from "./scenes/billing/SelfServicePortal";
import saasApi from "./utils/saasApi";
import { hasPermission, normalizeRole } from "./utils/permissions";

const Dashboard = lazy(() => import("./scenes/dashboard/DashboardPro"));
const VideoWall = lazy(() => import("./scenes/videowall/VideoWall"));
const MonthlyReports = lazy(() => import("./scenes/reports/MonthlyReports"));

const ProtectedRoute = ({ children, allowedRoles, userRole }) => {
  if (!allowedRoles.includes(userRole)) return <Navigate to="/overview" replace />;
  return children;
};

const SuspenseFallback = () => (
  <Box sx={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
    <CircularProgress />
  </Box>
);

function App({ user, role, onLogout }) {
  const [theme, colorMode] = useMode();
  const [access, setAccess] = useState({ loading: true, required: false, error: '' });
  const refreshAccess = useCallback(() => {
    setAccess((previous) => ({ ...previous, loading: true, error: '' }));
    saasApi.getOnboardingAccess()
      .then((result) => setAccess({ loading: false, required: Boolean(result.onboarding_required), error: '' }))
      .catch((error) => setAccess({ loading: false, required: false, error: error.message || 'No se pudo verificar el acceso' }));
  }, []);
  useEffect(() => { refreshAccess(); }, [refreshAccess]);
  const { t } = useTranslation();
  const userRole = normalizeRole(role || "viewer");
  const isVigilante = userRole === "vigilante";
  const managerRoles = ["owner", "admin", "superadmin"].filter((candidate) => hasPermission(candidate, "users:manage"));
  const readOnlyRoles = ["owner", "admin", "superadmin", "auditor"].filter((candidate) => hasPermission(candidate, "alerts:view"));
  const clipRoles = ["owner", "admin", "superadmin", "auditor"].filter((candidate) => hasPermission(candidate, "clips:view"));
  const billingRoles = ["owner", "admin", "superadmin"].filter((candidate) => hasPermission(candidate, "billing:manage"));
  const auditRoles = ["owner", "admin", "superadmin"].filter((candidate) => hasPermission(candidate, "audit:view"));

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {access.loading ? <SuspenseFallback /> : access.error ? <Stack spacing={2} sx={{ p: 4 }}><Alert severity="error">{access.error}</Alert><Button onClick={refreshAccess}>Reintentar</Button><Button onClick={onLogout}>Salir</Button></Stack> : access.required ? <SelfServicePortal onRefresh={refreshAccess} onLogout={onLogout} /> :
        <PlatformProvider>
          <AppShell user={user} role={userRole} onLogout={onLogout} background={theme.custom.pageGradient}>
            <Suspense fallback={<SuspenseFallback />}>
              <Routes>
                {isVigilante ? (
                  <>
                    <Route path="/" element={<VigilanteDashboard />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </>
                ) : (
                  <>
                    <Route path="/" element={<Navigate to="/overview" replace />} />
                    <Route path="/overview" element={<ExecutiveOverview />} />
                    <Route path="/monitoring/operations" element={<OperationsCenter />} />

                    <Route path="/monitoring" element={<Navigate to="/monitoring/live" replace />} />
                    <Route path="/monitoring/live" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <MonitoringModulePage title={t("platform.pages.live.title")} subtitle={t("platform.pages.live.subtitle")}>
                          <Dashboard />
                        </MonitoringModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/monitoring/cameras" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <MonitoringModulePage title={t("platform.pages.cameras.title")} subtitle={t("platform.pages.cameras.subtitle")}>
                          <UnifiedCameras />
                        </MonitoringModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/monitoring/health" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <CameraHealthPage role={userRole} />
                      </ProtectedRoute>
                    } />
                    <Route path="/monitoring/map" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <StoreMapPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/monitoring/video-wall" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <VideoWall />
                      </ProtectedRoute>
                    } />
                    <Route path="/monitoring/recordings" element={
                      <ProtectedRoute allowedRoles={clipRoles} userRole={userRole}>
                        <RecordingsPage />
                      </ProtectedRoute>
                    } />

                    <Route path="/incidents" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <IncidentsModulePage title={t("platform.pages.alerts.title")} subtitle={t("platform.pages.alerts.subtitle")}>
                          <ProfessionalAlerts />
                        </IncidentsModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/incidents/cases" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <CasesPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/incidents/evidence" element={
                      <ProtectedRoute allowedRoles={clipRoles} userRole={userRole}>
                        <EvidencePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/incidents/timeline" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <TimelinePage />
                      </ProtectedRoute>
                    } />

                    <Route path="/intelligence" element={<Navigate to="/intelligence/map" replace />} />
                    <Route path="/intelligence/map" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <IntelligenceMapPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/intelligence/heatmaps" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <HeatmapsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/intelligence/forensic-search" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <ForensicSearchPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/intelligence/person-journey" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <PersonJourneyPage />
                      </ProtectedRoute>
                    } />

                    <Route path="/analytics" element={<Navigate to="/analytics/detections" replace />} />
                    <Route path="/analytics/loss-prevention" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <LossPreventionPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/analytics/detections" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <DetectionReportsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/analytics/retail" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <DetectionReportsPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/analytics/stores" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <StoreComparisonPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/analytics/ai" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <AiPerformancePage />
                      </ProtectedRoute>
                    } />
                    <Route path="/analytics/reports" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <AnalyticsModulePage title={t("platform.pages.reports.title")} subtitle={t("platform.pages.reports.subtitle")}>
                          <MonthlyReports />
                        </AnalyticsModulePage>
                      </ProtectedRoute>
                    } />

                    <Route path="/system" element={<Navigate to={userRole === "auditor" ? "/system/health" : "/system/edge"} replace />} />
                    <Route path="/system/edge" element={
                      <ProtectedRoute allowedRoles={managerRoles} userRole={userRole}>
                        <SystemModulePage role={userRole} title="Edge / Dispositivos" subtitle="Vinculacion y camaras nuevas sin activar video">
                          <EdgeDevicesPage />
                        </SystemModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/stores" element={
                      <ProtectedRoute allowedRoles={managerRoles} userRole={userRole}>
                        <SystemModulePage role={userRole} title={t("platform.pages.stores.title")} subtitle={t("platform.pages.stores.subtitle")}>
                          <Onboarding />
                        </SystemModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/cameras" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <SystemModulePage role={userRole} title={t("platform.pages.cameraAdmin.title")} subtitle={t("platform.pages.cameraAdmin.subtitle")}>
                          <UnifiedCameras />
                        </SystemModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/users" element={
                      <ProtectedRoute allowedRoles={managerRoles} userRole={userRole}>
                        <SystemModulePage role={userRole} title={t("platform.pages.users.title")} subtitle={t("platform.pages.users.subtitle")}>
                          <Users />
                        </SystemModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/ai-config" element={
                      <ProtectedRoute allowedRoles={managerRoles} userRole={userRole}>
                        <SystemModulePage role={userRole} title={t("platform.pages.aiConfig.title")} subtitle={t("platform.pages.aiConfig.subtitle")}>
                          <CameraAIConfig />
                        </SystemModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/notifications" element={
                      <ProtectedRoute allowedRoles={managerRoles} userRole={userRole}>
                        <SystemModulePage role={userRole} title={t("platform.pages.notifications.title")} subtitle={t("platform.pages.notifications.subtitle")}>
                          <NotificationContacts />
                        </SystemModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/health" element={
                      <ProtectedRoute allowedRoles={readOnlyRoles} userRole={userRole}>
                        <SystemModulePage role={userRole} title={t("platform.pages.systemHealth.title")} subtitle={t("platform.pages.systemHealth.subtitle")}>
                          <SystemHealth />
                        </SystemModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/audit" element={
                      <ProtectedRoute allowedRoles={auditRoles} userRole={userRole}>
                        <SystemModulePage role={userRole} title={t("platform.pages.audit.title")} subtitle={t("platform.pages.audit.subtitle")}>
                          <AuditLog />
                        </SystemModulePage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/advanced" element={
                      <ProtectedRoute allowedRoles={managerRoles} userRole={userRole}>
                        <SystemAdvancedPage role={userRole}><DetectionsYamlEditor /></SystemAdvancedPage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/advanced/cameras" element={
                      <ProtectedRoute allowedRoles={managerRoles} userRole={userRole}>
                        <SystemAdvancedPage role={userRole}><CamerasYamlEditor /></SystemAdvancedPage>
                      </ProtectedRoute>
                    } />
                    <Route path="/system/advanced/ai-visual" element={
                      <ProtectedRoute allowedRoles={managerRoles} userRole={userRole}>
                        <SystemAdvancedPage role={userRole}><VisualAIConfig /></SystemAdvancedPage>
                      </ProtectedRoute>
                    } />

                    <Route path="/superadmin" element={
                      <ProtectedRoute allowedRoles={["superadmin"]} userRole={userRole}>
                        <SuperadminConsole />
                      </ProtectedRoute>
                    } />
                    <Route path="/superadmin/*" element={
                      <ProtectedRoute allowedRoles={["superadmin"]} userRole={userRole}>
                        <SuperadminConsole />
                      </ProtectedRoute>
                    } />
                    <Route path="/support" element={
                      <ProtectedRoute allowedRoles={["owner", "admin", "superadmin", "auditor", "vigilante"]} userRole={userRole}>
                        <SupportCenter />
                      </ProtectedRoute>
                    } />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/calendar" element={<Calendar />} />
                    <Route path="/manageFiles" element={
                      <ProtectedRoute allowedRoles={clipRoles} userRole={userRole}>
                        <ManageFiles />
                      </ProtectedRoute>
                    } />
                    <Route path="/billing" element={
                      <ProtectedRoute allowedRoles={billingRoles} userRole={userRole}>
                        <Billing />
                      </ProtectedRoute>
                    } />
                    <Route path="/payment-success" element={<Navigate to="/billing?payment_return=1" replace />} />

                    <Route path="/statistics" element={<Navigate to="/analytics/retail" replace />} />
                    <Route path="/reports" element={<Navigate to="/analytics/reports" replace />} />
                    <Route path="/cameras" element={<Navigate to="/monitoring/cameras" replace />} />
                    <Route path="/alerts" element={<Navigate to="/incidents" replace />} />
                    <Route path="/video-wall" element={<Navigate to="/monitoring/video-wall" replace />} />
                    <Route path="/onboarding" element={<Navigate to="/system/stores" replace />} />
                    <Route path="/ai-config" element={<Navigate to="/system/ai-config" replace />} />
                    <Route path="/system-health" element={<Navigate to="/system/health" replace />} />
                    <Route path="/audit" element={<Navigate to="/system/audit" replace />} />
                    <Route path="/usuarios" element={<Navigate to="/system/users" replace />} />
                    <Route path="/config/contactos" element={<Navigate to="/system/notifications" replace />} />
                    <Route path="/config/detections" element={<Navigate to="/system/advanced" replace />} />
                    <Route path="/config/cameras" element={<Navigate to="/system/advanced/cameras" replace />} />
                    <Route path="/config/ai-visual" element={<Navigate to="/system/advanced/ai-visual" replace />} />
                    <Route path="/recentalerts" element={<Navigate to="/incidents" replace />} />
                    <Route path="/camera-management" element={<Navigate to="/monitoring/cameras" replace />} />
                    <Route path="/team" element={<Navigate to="/system/users" replace />} />
                    <Route path="/buildingInfo" element={<Navigate to="/monitoring/cameras" replace />} />
                    <Route path="/floorInfo" element={<Navigate to="/monitoring/cameras" replace />} />
                    <Route path="/cameraInfo" element={<Navigate to="/monitoring/cameras" replace />} />
                    <Route path="/contacts" element={<Navigate to="/incidents" replace />} />
                    <Route path="/pie" element={<Navigate to="/analytics/retail" replace />} />
                    <Route path="/line" element={<Navigate to="/analytics/retail" replace />} />
                    <Route path="/bar" element={<Navigate to="/analytics/retail" replace />} />
                    <Route path="/cameracount" element={<Navigate to="/monitoring/cameras" replace />} />
                    <Route path="/cameracounts" element={<Navigate to="/monitoring/cameras" replace />} />
                    <Route path="*" element={<Navigate to="/overview" replace />} />
                  </>
                )}
              </Routes>
            </Suspense>
          </AppShell>
        </PlatformProvider>
        }
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

const AppWithAuth = () => (
  <AuthWrapper>
    <App />
  </AuthWrapper>
);

export default AppWithAuth;
